import { Component, Directive, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { InputStateDirective } from 'directives/input-state.directive';
import { ValidationDirective } from 'directives/validation.directive';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { MessageType } from 'enums/MessageType';
import { PriceAdjustmentModel } from 'models/generated/Entities.Accounting.PriceAdjustment';
import { PriceAdjustmentValueModel } from 'models/generated/Entities.Accounting.PriceAdjustmentValue';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { CredentialRequired } from 'services/credential.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { PriceAdjustmentService } from 'services/price-adjustment.service';
import { PushService } from 'services/push.service';
import { RelationService } from 'services/relation.service';

@Component({
	templateUrl: './list-price-adjustment-tables.component.html',
	styleUrls: ['./list-price-adjustment-tables.component.scss'],
	imports: [Layout, ListView, CustomInputComponent, InputStateDirective, FormsModule, ValidationDirective, TranslatePipe]
})
@ComponentUrl('price-adjustments')
@CredentialRequired(CredentialType.EditPriceAdjustments)
export class ListPriceAdjustmentTablesComponent implements OnDestroy {

	inputType = InputType;
	selectedAdjustment?: PriceAdjustmentModel;
	priceAdjustments?: PriceAdjustmentModel[];
	sortedIntervals: { value: PriceAdjustmentValueModel, lowerBound: number }[] = [];

	@ViewChild(SubmitButtonComponent) submitButton!: SubmitButtonComponent;

	private changeSubscription: Subscription;
	private _adjustmentId?: string;
	@UrlParam
	set adjustmentId(id: string | undefined) {
		this._adjustmentId = id;
		if (id) this.setSelectedAdjustmentFromId(id);
	}
	get adjustmentId() {
		return this._adjustmentId;
	}
	private setSelectedAdjustmentFromId(adjustmentId: string) {
		let matchingAdjustment: PriceAdjustmentModel | undefined;
		if (this.priceAdjustments && (matchingAdjustment = this.priceAdjustments.find(t => t.id === adjustmentId))) {
			this.select(matchingAdjustment);
		}
	}

	constructor(private readonly priceAdjustmentService: PriceAdjustmentService, private readonly navigationService: NavigationService, pushService: PushService) {
		priceAdjustmentService.getAllPriceAdjustments().then(priceAdjustmentTables => {
			this.priceAdjustments = priceAdjustmentTables;
			if (this._adjustmentId) this.setSelectedAdjustmentFromId(this._adjustmentId);
		});

		this.changeSubscription = pushService.onChanges<{ priceAdjustmentId: string }>(MessageType.PriceAdjustmentChanges).subscribe(event => {
			priceAdjustmentService.getAllPriceAdjustments().then(priceAdjustmentTables => {
				if (!this.priceAdjustments) return;

				const matchingIndex = this.priceAdjustments.findIndex(pa => pa.id == event.data?.priceAdjustmentId);
				if (matchingIndex >= 0) Object.assign(this.priceAdjustments[matchingIndex], priceAdjustmentTables.find(pa => pa.id == event.data?.priceAdjustmentId));
			});
		});
	}
	ngOnDestroy(): void {
		this.changeSubscription.unsubscribe();
	}

	sortAdjustmentIntervals() {
		if (!this.selectedAdjustment) return;
		const sorteValues = [...this.selectedAdjustment.priceAdjustmentValues];
		sorteValues.sort((a, b) => a.upperBound - b.upperBound);
		this.sortedIntervals = sorteValues.map((value, index) => { return { value: value, lowerBound: sorteValues[index - 1]?.upperBound ?? 0 } });
	}

	removeValue(adjustmentValue: PriceAdjustmentValueModel, valueCollection: PriceAdjustmentValueModel[]) {
		const index = valueCollection.findIndex(v => v === adjustmentValue);
		if (index >= 0) valueCollection.splice(index, 1);
		this.sortAdjustmentIntervals();
	}

	addNewValue(adjustmentValues: PriceAdjustmentValueModel[]) {
		const lowerBound = (!this.sortedIntervals || this.sortedIntervals.length <= 0) ? 0 : this.sortedIntervals[this.sortedIntervals.length - 1].value.upperBound;
		adjustmentValues.push(Object.assign(new PriceAdjustmentValueModel(), { upperBound: lowerBound + 50, targetValue: lowerBound + 50 }));
		this.sortAdjustmentIntervals();
	}

	select(priceAdjustment: PriceAdjustmentModel) {
		const cloneAdjustment = new PriceAdjustmentModel();
		Object.assign(cloneAdjustment, RelationService.deepClone(priceAdjustment));

		this.selectedAdjustment = cloneAdjustment;
		this._adjustmentId = cloneAdjustment.id;
		this.sortAdjustmentIntervals();

		this.navigationService.refreshUrl();
	}

	save() {
		if (this.selectedAdjustment) this.priceAdjustmentService.savePriceAdjustment(this.selectedAdjustment).then(() => this.submitButton.succeeded());
	}
}
@Directive({
	selector: '[lowerBound]',
	exportAs: 'lowerBound'
})
export class LowerBoundDirective {
	@Input('lowerBound') public value = 0;
}
